import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { enhanceColors, getTaskStatus } from '../../services/chatApi'; // Import getTaskStatus
import { Download, Upload, Sliders, RefreshCw, Loader2, Sun, Palette, Contrast, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const EnhanceColors = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();    
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const pollInterval = useRef(null);

    // Parameters
    const [saturation, setSaturation] = useState(1.0);
    const [brightness, setBrightness] = useState(0);
    const [contrast, setContrast] = useState(0);

    // Cleanup polling
    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);

    const resetParams = useCallback(() => {
        setSaturation(1.0);
        setBrightness(0);
        setContrast(0);
        setError(null);
        clearInterval(pollInterval.current);
    }, []);

    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            resetParams();
        }
    };

    const handleProcess = async () => {
        if (!file) return;
        
        setIsLoading(true);
        setError(null);
        setProcessedImage(null);
        
        try {
            // Start Async Task
            const startResponse = await enhanceColors(file, saturation, brightness, contrast);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            // Poll for completion
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        const result = statusRes.result || statusRes.info;
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                            setIsLoading(false);
                        } else {
                            setError("Invalid response format.");
                            setIsLoading(false);
                        }

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('enhanceColors.errors.generic'));
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error.");
                }
            }, 2000); // Poll every 2 seconds

        } catch (err) {
            setError(t('enhanceColors.errors.generic'));
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = "enhanced_image.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            window.open(processedImage, '_blank');
        }
    };

    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        resetParams();
    };

    
    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('enhanceColors.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-2xl">
                    {t('enhanceColors.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="enhance-upload" />
                        <label htmlFor="enhance-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('enhanceColors.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('enhanceColors.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Controls Column */}
                        <div className="lg:col-span-4 order-2 lg:order-1 space-y-6">
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                <div className="flex items-center gap-2 mb-4 text-brand-orange">
                                    <Sliders size={20} />
                                    <h4 className="font-semibold">{t('enhanceColors.controls.title')}</h4>
                                </div>
                                
                                {/* Saturation */}
                                <div className="mb-6">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                            <Palette size={14} /> {t('enhanceColors.controls.saturation')}
                                        </label>
                                        <span className="text-sm text-slate-500">{saturation}</span>
                                    </div>
                                    <input 
                                        type="range" min="0" max="3" step="0.1" 
                                        value={saturation} 
                                        disabled={isLoading}
                                        onChange={(e) => setSaturation(parseFloat(e.target.value))} 
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange disabled:opacity-50" 
                                    />
                                </div>

                                {/* Brightness */}
                                <div className="mb-6">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                            <Sun size={14} /> {t('enhanceColors.controls.brightness')}
                                        </label>
                                        <span className="text-sm text-slate-500">{brightness}</span>
                                    </div>
                                    <input 
                                        type="range" min="-100" max="100" step="1" 
                                        value={brightness} 
                                        disabled={isLoading}
                                        onChange={(e) => setBrightness(parseInt(e.target.value))} 
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange disabled:opacity-50" 
                                    />
                                </div>

                                {/* Contrast */}
                                <div className="mb-6">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                            <Contrast size={14} /> {t('enhanceColors.controls.contrast')}
                                        </label>
                                        <span className="text-sm text-slate-500">{contrast}</span>
                                    </div>
                                    <input 
                                        type="range" min="-100" max="100" step="1" 
                                        value={contrast} 
                                        disabled={isLoading}
                                        onChange={(e) => setContrast(parseInt(e.target.value))} 
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange disabled:opacity-50" 
                                    />
                                </div>

                                <div className="pt-4 border-t border-slate-100">
                                    {!processedImage ? (
                                        <button 
                                            onClick={handleProcess} 
                                            disabled={isLoading} 
                                            className="w-full bg-brand-orange text-white py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-70"
                                        >
                                            {isLoading ? <Loader2 className="animate-spin" /> : <Sparkles size={18} />} 
                                            {t('enhanceColors.buttons.apply')}
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            <button onClick={handleDownload} className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2">
                                                <Download size={18} /> {t('enhanceColors.buttons.download')}
                                            </button>
                                            <button onClick={() => setProcessedImage(null)} className="w-full bg-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-300 transition flex items-center justify-center gap-2">
                                                <Sliders size={18} /> {t('enhanceColors.buttons.edit')}
                                            </button>
                                        </div>
                                    )}
                                    <button onClick={resetTool} className="w-full mt-3 bg-slate-100 text-slate-700 py-2 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2">
                                        <RefreshCw size={18} /> {t('enhanceColors.buttons.newImage')}
                                    </button>
                                </div>
                            </div>

                            {error && <div className="text-center p-3 bg-red-50 text-red-600 rounded-md border border-red-200 text-sm">{error}</div>}
                        </div>

                        {/* Images Column */}
                        <div className="lg:col-span-8 order-1 lg:order-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm font-semibold text-slate-500 mb-2">{t('enhanceColors.labels.original')}</p>
                                    <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                                        <img src={previewUrl} alt="Original" className="w-full h-auto" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-brand-orange mb-2">{t('enhanceColors.labels.result')}</p>
                                    <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200 min-h-[200px] flex items-center justify-center relative">
                                        {isLoading && (
                                            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                                                <Loader2 className="animate-spin text-brand-orange w-10 h-10" />
                                            </div>
                                        )}
                                        {processedImage ? (
                                            <img src={processedImage} alt="Enhanced" className="w-full h-auto" />
                                        ) : (
                                            <div className="opacity-50 grayscale">
                                                <img src={previewUrl} alt="Preview" className="w-full h-auto" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Tool Navigator inserted here */}
                            {processedImage && (
                                <ToolNavigator currentImage={processedImage} />
                            )}
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default EnhanceColors;
